import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {InputField} from '@payactive/app-common';
import React, {Fragment, ReactElement, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AlertContext} from '../../../components/contexts/WithAlertContext';
import ApiKeyCreatedScreen from './ApiKeyCreatedScreen';
import ApiKeyService from "../../../services/ApiKeyService";

enum States {
  CREATE = 'CREATE',
  SUCCESS = 'SUCCESS',
}

interface CreateApiKeyDialogProps {
  visible: boolean;
  onClose: () => void;
}

export default function CreateApiKeyDialog({ visible, onClose }: CreateApiKeyDialogProps): ReactElement {
  const { t } = useTranslation('developer');

  const alerting = useContext(AlertContext);

  const [isVisible, setVisibility] = useState<boolean>(false);
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const [apiKeyNote, setApiKeyNote] = useState<string>('');
  const [apiKeyExpiresAt, setApiKeyExpiresAt] = useState<string>('');
  const [state, setState] = useState<States>(States.CREATE);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const handleClose = (): void => {
    onClose();
    if (state === States.SUCCESS) {
      alerting.sendAlert('success', t('api key created'));
    }
  };

  useEffect(() => {
    setVisibility(visible);
  }, [visible]);

  const onCreate = (): void => {
    const expiresAt = apiKeyExpiresAt ? new Date(apiKeyExpiresAt).toISOString() : undefined;
    ApiKeyService.createApiKey({name: apiKeyName, note: apiKeyNote, expiresAt, scopes:["read", "write"]}).then((res) => {
      if (res) {
        setApiKey(res.plainTextApiKey);
        setState(States.SUCCESS);
      }
    });
  };

  return (
    <Dialog
      open={isVisible}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          return false;
        }
        handleClose();
      }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('create api key')}</DialogTitle>
      <DialogContent>
        {state === States.CREATE && (
          <Fragment>
            <DialogContentText>{t('choose a name for the new api key')}</DialogContentText>
            <InputField id="name" name="name" label={t('apikeyname')} type="text" value={apiKeyName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKeyName(e.target.value)} fullWidth />
            <InputField id="note" name="note" label={t('note')} type="text" value={apiKeyNote} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKeyNote(e.target.value)} fullWidth />
            <InputField id="expiresAt" name="expiresAt" label={t('expiresAt')} type="datetime-local" value={apiKeyExpiresAt} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKeyExpiresAt(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Fragment>
        )}
        {state === States.SUCCESS && apiKey && <ApiKeyCreatedScreen apiKey={apiKey} />}
      </DialogContent>
      <DialogActions>
        {state === States.CREATE && (
          <Fragment>
            <Button onClick={handleClose} color="primary">
              {t('global:cancel')}
            </Button>
            <Button disabled={apiKeyName === ''} variant="contained" onClick={onCreate} color="primary">
              {t('global:create')}
            </Button>
          </Fragment>
        )}
        {state === States.SUCCESS && (
          <Button variant="contained" onClick={handleClose} color="primary">
            {t('global:close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
